import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import { isAfter } from "date-fns";

import { createReplaceAllReducer } from "store/utils";
import { HomeRoleType } from "utils/urls";

export const LOCAL_STATE_KEY = "monarch-local";

export type OnBoardingCourse = {
  id: string;
  isClosed: boolean;
  hasViewedGuide: boolean;
  hasImported: boolean;
  hasUpdatedTitleAndDescription: boolean;
  hasOutcome: boolean;
  hasTopic: boolean;
};

type OnBoardingCourses = {
  [id: string]: OnBoardingCourse;
};

export type AutoMuteScenario =
  | "initial_join"
  | "refresh"
  | "breakout_listening"
  | "breakout_return";

export type LocalAvState = {
  isMicEnabled: boolean;
  isCameraEnabled: boolean;
  preferredMicDeviceLabel?: string;
  preferredCameraDeviceLabel?: string;
  isBackgroundBlurEnabled: boolean;
  wasAutoMuted: boolean;
  lastAutoMuteScenario?: AutoMuteScenario;
};

export type LocalState = {
  onBoardingCourses: OnBoardingCourses;
  isSidebarOpen: boolean;
  editMode: boolean;
  /* scroll positions for persistent scroll containers */
  scrollPositions: { [key: string]: number };
  localAvState: LocalAvState;
  /* datetime of the last read message in class chat */
  classChatReadAt: { [courseId: string]: string };
  /* the last home view role we viewed the home page as */
  lastHomeRole: HomeRoleType | null;
  /* the last view option we viewed the courses list as */
  lastCourseViewOption: "course_code" | "term" | null;
  /* last API error encountered */
  apiError: { model: string; action: string; errorMessage: string } | null;
  /* flag to show version mismatch warning */
  showVersionMismatchWarning: boolean;
  /* flag to track if we've already handled a version mismatch for this page load */
  hasHandledVersionMismatch: boolean;
};

const defaultState: LocalState = {
  onBoardingCourses: {},
  isSidebarOpen: true,
  editMode: false,
  scrollPositions: {},
  localAvState: {
    isMicEnabled: true,
    isCameraEnabled: true,
    isBackgroundBlurEnabled: false,
    wasAutoMuted: false,
  },
  classChatReadAt: {},
  lastHomeRole: null,
  lastCourseViewOption: "course_code",
  apiError: null,
  showVersionMismatchWarning: false,
  hasHandledVersionMismatch: false,
};

export const retrieveLocalState = () => {
  const serializedState = localStorage.getItem(LOCAL_STATE_KEY);
  const storedState = (serializedState ? JSON.parse(serializedState) : {}) as Partial<LocalState>;
  return {
    ...defaultState,
    ...storedState,
  };
};

const saveLocalState = (state: LocalState) => {
  localStorage.setItem(LOCAL_STATE_KEY, JSON.stringify(state));
};

const deleteLocalState = () => {
  localStorage.removeItem(LOCAL_STATE_KEY);
};

const initialState = retrieveLocalState;

// TODO: add "saveLocalState" as a middleware for all reducers in this slice
export const LocalStore = createSlice({
  name: "local",
  initialState,
  reducers: {
    setOnBoardingCourse: (state, action: PayloadAction<OnBoardingCourse>) => {
      state.onBoardingCourses[action.payload.id] = action.payload;
      saveLocalState(state);
    },
    setIsSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.isSidebarOpen = action.payload;
      saveLocalState(state);
    },
    setIsEditMode: (state, action: PayloadAction<boolean>) => {
      state.editMode = action.payload;
      saveLocalState(state);
    },
    setLastHomeRole: (state, action: PayloadAction<HomeRoleType | null>) => {
      state.lastHomeRole = action.payload;
      saveLocalState(state);
    },
    setLastCourseViewOption: (state, action: PayloadAction<"course_code" | "term" | null>) => {
      state.lastCourseViewOption = action.payload;
      saveLocalState(state);
    },
    setScrollPosition: (state, action: PayloadAction<{ id: string; position: number }>) => {
      state.scrollPositions[action.payload.id] = action.payload.position;
      saveLocalState(state);
    },
    setLocalMicIsEnabled: (state, action: PayloadAction<boolean>) => {
      state.localAvState.isMicEnabled = action.payload;
      saveLocalState(state);
    },
    setLocalCameraIsEnabled: (state, action: PayloadAction<boolean>) => {
      state.localAvState.isCameraEnabled = action.payload;
      saveLocalState(state);
    },
    setPreferredMicDeviceLabel: (state, action: PayloadAction<string | undefined>) => {
      state.localAvState.preferredMicDeviceLabel = action.payload;
      saveLocalState(state);
    },
    setPreferredCameraDeviceLabel: (state, action: PayloadAction<string | undefined>) => {
      state.localAvState.preferredCameraDeviceLabel = action.payload;
      saveLocalState(state);
    },
    setIsBackgroundBlurEnabled: (state, action: PayloadAction<boolean>) => {
      state.localAvState.isBackgroundBlurEnabled = action.payload;
      saveLocalState(state);
    },
    setWasAutoMuted: (state, action: PayloadAction<boolean>) => {
      state.localAvState.wasAutoMuted = action.payload;
      saveLocalState(state);
    },
    setLastAutoMuteScenario: (state, action: PayloadAction<AutoMuteScenario | undefined>) => {
      state.localAvState.lastAutoMuteScenario = action.payload;
      saveLocalState(state);
    },
    updateClassChatMessagesReadAt: (
      state,
      action: PayloadAction<{ courseId: string; readAt: string }>,
    ) => {
      const { courseId, readAt } = action.payload;
      const lastReadAt = state.classChatReadAt[courseId];
      if (!lastReadAt || isAfter(readAt, lastReadAt)) {
        state.classChatReadAt[courseId] = readAt;
        saveLocalState(state);
      }
    },
    setApiError: (
      state,
      action: PayloadAction<{ model: string; action: string; errorMessage: string } | null>,
    ) => {
      state.apiError = action.payload;
      saveLocalState(state);
    },
    setShowVersionMismatchWarning: (state, action: PayloadAction<boolean>) => {
      state.showVersionMismatchWarning = action.payload;
      saveLocalState(state);
    },
    setHasHandledVersionMismatch: (state, action: PayloadAction<boolean>) => {
      state.hasHandledVersionMismatch = action.payload;
      saveLocalState(state);
    },
  },
  extraReducers: createReplaceAllReducer<LocalState>("local", initialState, () => {
    deleteLocalState();
  }),
});

export const localStateActions = LocalStore.actions;
